/**
 * P&L Report Page
 *
 * Cash basis P&L report showing revenue, expenses, and profit metrics.
 * Revenue is recognized when payment succeeds (not when order is placed).
 *
 * Features:
 * - Summary cards with key metrics
 * - Revenue/Expenses trend chart
 * - Order count chart
 * - Daily profit chart
 * - Expense breakdown table
 * - Auto-refresh every 15 minutes
 * - Aggregate view when "All Clinics" is selected
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { format, parse } from 'date-fns'
import { Card, CardHeader, CardTitle, CardContent, Button } from '../components/ui'
import { DateRangePicker } from '../components/DateRangePicker'
import { dashboardAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import AggregatePnLView from '../components/AggregatePnLView'
import DataFreshnessWarning from '../components/DataFreshnessWarning'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Area
} from 'recharts'
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  ShoppingCart,
  Package,
  RefreshCw,
  AlertCircle,
  Loader2
} from 'lucide-react'

// Constants
const AUTO_REFRESH_INTERVAL = 15 * 60 * 1000 // 15 minutes

// Default to last 30 days
const getDefaultDateRange = () => {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - 29)
  return { from: start, to: end }
}

/**
 * Format cents to dollars display
 */
function formatCurrency(cents, compact = false) {
  const dollars = cents / 100
  if (compact && Math.abs(dollars) >= 1000) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(dollars)
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(dollars)
}

/**
 * Format number with commas
 */
function formatNumber(num) {
  return new Intl.NumberFormat('en-US').format(num)
}

/**
 * Delta Badge - shows prior-period percentage change
 * invert: for cost metrics where increase = bad (red)
 */
function DeltaBadge({ current, prior, invert = false }) {
  if (!prior || prior === 0) return null
  const pct = ((current - prior) / prior) * 100
  if (!isFinite(pct)) return null
  const isPositive = pct > 0
  const isGood = invert ? !isPositive : isPositive
  return (
    <span className={`inline-flex items-center text-xs font-medium ${isGood ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
      {isPositive ? '↑' : '↓'} {Math.abs(pct).toFixed(1)}%
    </span>
  )
}

/**
 * Summary Card Component
 */
function SummaryCard({ title, value, subtitle, icon: Icon, trend, trendDirection, delta, loading }) {
  const isPositive = trendDirection === 'up'
  const TrendIcon = isPositive ? TrendingUp : TrendingDown

  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-6">
        {loading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-8 bg-muted rounded w-3/4"></div>
            <div className="h-3 bg-muted rounded w-1/3"></div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">{title}</span>
              {Icon && (
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold tracking-tight">{value}</span>
              {delta}
            </div>
            {(subtitle || trend !== undefined) && (
              <div className="flex items-center gap-2 mt-1">
                {trend !== undefined && (
                  <span className={`flex items-center text-xs font-medium ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    <TrendIcon className="h-3 w-3 mr-0.5" />
                    {trend}%
                  </span>
                )}
                {subtitle && (
                  <span className="text-xs text-muted-foreground">{subtitle}</span>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Custom Tooltip for Charts
 */
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null
  
  return (
    <div className="bg-popover border border-border rounded-lg shadow-lg p-3 min-w-[180px]">
      <p className="font-medium text-sm mb-2">{label}</p>
      <div className="space-y-1">
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5">
              <span 
                className="w-2 h-2 rounded-full" 
                style={{ backgroundColor: entry.color }}
              />
              {entry.name}
            </span>
            <span className="font-medium">
              {entry.name.includes('Orders') ? formatNumber(entry.value) : formatCurrency(entry.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Unit Economics - the period split by patient: new (first-ever purchase in
 * the period) vs recurring (everyone else charged or shipped to), as totals
 * and per patient. All ad spend sits with new patients, so new ad spend per
 * patient is CAC. Built by backend services/pnlUnitEconomics.
 */
const UNIT_ROWS = [
  { key: 'patients', label: 'Patients', count: true },
  { key: 'revenue', label: 'Revenue' },
  { key: 'cogs', label: 'COGS', cost: true },
  { key: 'shipping', label: 'Shipping', cost: true },
  { key: 'ad_spend', label: 'Ad Spend', perLabel: 'CAC', cost: true },
  { key: 'ghost_fee', label: 'Ghost', fee: 'ghost', cost: true },
  { key: 'sales_commission', label: 'Sales Commission', fee: 'sales_commission', cost: true },
  { key: 'contribution', label: 'Net Rev', strong: true },
]

const FEE_BASES = {
  ghost: fee => `${Math.round(fee.rate * 100)}% of ${fee.channels.map(c => c[0].toUpperCase() + c.slice(1)).join(' + ')} spend`,
  sales_commission: fee => `${Math.round(fee.rate * 100)}% of new rep + Reddit sales`,
}

function UnitEconomicsCard({ data, prior, loading }) {
  // A fee row shows only for a clinic that has the fee.
  const rows = UNIT_ROWS.filter(row => !row.fee || data?.fees?.[row.fee])
  const cell = (group, row, perPatient) => {
    if (row.count) return perPatient ? '' : formatNumber(group?.patients || 0)
    // An unread base is unavailable, not a $0 fee; recurring patients carry none.
    if (row.fee && !data?.fees?.[row.fee]?.checked) return '—'
    const value = perPatient ? group?.per_patient?.[row.key] : group?.[row.key]
    return value == null ? '—' : formatCurrency(value)
  }
  const tone = (row, value) =>
    row.key === 'contribution' && value != null
      ? (value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')
      : ''
  const groups = [
    { key: 'new', label: 'New Patients', hint: 'First-ever purchase in this period' },
    { key: 'recurring', label: 'Recurring Patients', hint: 'Charged or shipped to, but bought before' },
  ]

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">Unit Economics · New vs Recurring Patients</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          New = first-ever purchase in this period; all ad spend sits with them, so their ad spend per patient is CAC.
          Net Rev = revenue − COGS − shipping − ad spend{data?.fees?.ghost ? ' − Ghost' : ''}{data?.fees?.sales_commission ? ' − sales commission' : ''}.
          {(data?.fees?.ghost || data?.fees?.sales_commission) && ' Fees are new-patient costs and are not in the P&L expenses above.'}
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="animate-pulse space-y-2">
            {[...Array(6)].map((_, i) => <div key={i} className="h-6 bg-muted rounded" />)}
          </div>
        ) : !data ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Not available for this clinic: its P&L source cannot split costs by patient.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 font-medium" rowSpan={2}></th>
                    {groups.map(g => (
                      <th key={g.key} colSpan={2} className="text-center py-1 px-2 font-medium" title={g.hint}>{g.label}</th>
                    ))}
                  </tr>
                  <tr className="border-b text-xs text-muted-foreground">
                    {groups.map(g => [
                      <th key={`${g.key}-t`} className="text-right py-1 px-2 font-normal">Total</th>,
                      <th key={`${g.key}-p`} className="text-right py-1 px-2 font-normal">Per Patient</th>,
                    ])}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr key={row.key} className={`border-b border-muted/50 ${row.strong ? 'font-semibold border-t-2 border-t-foreground/30' : ''}`}>
                      <td className="py-2 px-2 text-muted-foreground">
                        {row.label}
                        {row.perLabel && <span className="text-xs ml-1">(/pt = {row.perLabel})</span>}
                        {row.fee && data.fees?.[row.fee] && (
                          <span
                            className="block text-[11px]"
                            title={data.fees[row.fee].checked ? `Base: ${formatCurrency(data.fees[row.fee].base)}` : 'Base could not be read'}
                          >
                            {FEE_BASES[row.fee](data.fees[row.fee])}
                          </span>
                        )}
                      </td>
                      {groups.map(g => {
                        const group = data[g.key]
                        const per = group?.per_patient?.[row.key]
                        const priorPer = prior?.[g.key]?.per_patient?.[row.key]
                        return [
                          <td key={`${g.key}-t`} className={`text-right py-2 px-2 ${tone(row, group?.[row.key])}`}>
                            {cell(group, row, false)}
                            {row.count && (
                              <span className="block text-xs text-muted-foreground font-normal">
                                {formatNumber(group?.paying_patients || 0)} paid · {formatNumber(group?.fills || 0)} fills
                              </span>
                            )}
                          </td>,
                          <td key={`${g.key}-p`} className={`text-right py-2 px-2 ${tone(row, per)}`}>
                            {cell(group, row, true)}
                            {!row.count && per != null && priorPer > 0 && (
                              <span className="block">
                                <DeltaBadge current={per} prior={priorPer} invert={row.cost} />
                              </span>
                            )}
                          </td>,
                        ]
                      })}
                    </tr>
                  ))}
                  <tr className="text-xs text-muted-foreground">
                    <td className="py-2 px-2">Margin</td>
                    {groups.map(g => (
                      <td key={g.key} colSpan={2} className="text-right py-2 px-2">
                        {data[g.key]?.margin_percent == null ? '—' : `${data[g.key].margin_percent}%`}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            {data.unattributed_sales > 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                {formatCurrency(data.unattributed_sales)} of revenue names no patient (orphan payments, bundles not yet filled) and is in neither column.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default function AnalyticsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastRefresh, setLastRefresh] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const clinicId = searchParams.get('clinic') || 'tmates'
  const { currentClinicId } = useAuthStore()

  // Determine if we're in aggregate mode (All Clinics selected)
  const isAggregateMode = currentClinicId === null
  const effectiveClinicId = currentClinicId || clinicId
  
  // Parse date range from URL params or use default
  const dateRange = useMemo(() => {
    const startParam = searchParams.get('start')
    const endParam = searchParams.get('end')
    
    if (startParam && endParam) {
      try {
        const from = parse(startParam, 'yyyy-MM-dd', new Date())
        const to = parse(endParam, 'yyyy-MM-dd', new Date())
        if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
          return { from, to }
        }
      } catch (e) {
        console.error('[ANALYTICS] Error parsing date params:', e)
      }
    }
    return getDefaultDateRange()
  }, [searchParams])
  
  // Update URL when date range changes
  const setDateRange = useCallback((newRange) => {
    if (newRange?.from && newRange?.to) {
      setSearchParams(prev => {
        const params = new URLSearchParams(prev)
        params.set('start', format(newRange.from, 'yyyy-MM-dd'))
        params.set('end', format(newRange.to, 'yyyy-MM-dd'))
        return params
      })
    }
  }, [setSearchParams])
  
  // Fetch P&L data (single clinic or aggregate)
  const fetchData = useCallback(async (showRefreshIndicator = false) => {
    if (!dateRange?.from || !dateRange?.to) return

    try {
      if (showRefreshIndicator) {
        setIsRefreshing(true)
      } else {
        setLoading(true)
      }
      setError(null)

      // Format dates for API
      const startDate = format(dateRange.from, 'yyyy-MM-dd')
      const endDate = format(dateRange.to, 'yyyy-MM-dd')

      let response
      if (isAggregateMode) {
        console.log('[ANALYTICS] Fetching AGGREGATE P&L data, range:', startDate, 'to', endDate)
        response = await dashboardAPI.getAggregatePnL({ startDate, endDate })
      } else {
        console.log('[ANALYTICS] Fetching P&L data for clinic:', effectiveClinicId, 'range:', startDate, 'to', endDate)
        response = await dashboardAPI.getPnL({ startDate, endDate })
      }

      if (response.data?.success) {
        setData(response.data.data)
        setLastRefresh(new Date())
        console.log('[ANALYTICS] Data loaded successfully:', response.data.data.summary)
      } else {
        throw new Error(response.data?.error?.message || 'Failed to load analytics data')
      }
    } catch (err) {
      console.error('[ANALYTICS] Error fetching data:', err)
      setError(err.response?.data?.error?.message || err.message || 'Failed to load analytics data')
    } finally {
      setLoading(false)
      setIsRefreshing(false)
    }
  }, [effectiveClinicId, isAggregateMode, dateRange])
  
  // Initial load and clinic/range changes
  useEffect(() => {
    fetchData()
  }, [fetchData])
  
  // Auto-refresh timer
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('[ANALYTICS] Auto-refreshing data...')
      fetchData(true)
    }, AUTO_REFRESH_INTERVAL)
    
    return () => clearInterval(interval)
  }, [fetchData])
  
  // Prepare chart data
  const chartData = data?.daily?.map(day => {
    const newOrders = day.new_orders || 0
    const refillOrders = day.refill_orders || day.recurring_orders || 0
    const adSpend = day.ad_spend || 0
    // CAC = Ad Spend / New Orders (in dollars)
    const cac = newOrders > 0 ? Math.round(adSpend / newOrders) : 0
    
    return {
      date: day.displayDate,
      shortDate: day.shortDate,
      revenue: day.total_revenue,
      expenses: day.total_expenses,
      profit: day.profit,
      profit_gs_high: day.profit_gs_high,
      newOrders,
      refillOrders,
      newSales: day.new_sales || 0,
      refillSales: day.refill_sales || day.recurring_sales || 0,
      totalOrders: day.total_orders || 0,
      cogs: day.cogs,
      shipping: day.shipping,
      adSpend,
      cac
    }
  }) || []
  
  // Handle refresh button
  const handleRefresh = () => {
    fetchData(true)
  }
  
  // Error state
  if (error && !data) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">P&L Report</h1>
        </div>
        <Card className="p-8">
          <div className="flex flex-col items-center justify-center text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-semibold mb-2">Failed to Load P&L Report</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => fetchData()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  // Aggregate mode - render AggregatePnLView
  if (isAggregateMode) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        {/* Header - single row */}
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold truncate">P&L Report - All Brands</h1>
            <p className="text-xs text-muted-foreground hidden sm:block">
              Aggregate view across all clinics
              {lastRefresh && ` · ${lastRefresh.toLocaleTimeString()}`}
            </p>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <DateRangePicker
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
            />
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="h-8 w-8 sm:h-9 sm:w-9"
            >
              {isRefreshing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <DataFreshnessWarning
          daily={data?.daily}
          dateRange={dateRange}
          loading={loading}
        />

        {/* Aggregate View Component */}
        <AggregatePnLView
          data={data}
          loading={loading}
          errors={data?.errors}
        />
      </div>
    )
  }

  // Single clinic mode - existing implementation
  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header - single row */}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold truncate">P&L Report</h1>
          <p className="text-xs text-muted-foreground hidden sm:block">
            Cash basis • Revenue on payment
            {lastRefresh && ` · ${lastRefresh.toLocaleTimeString()}`}
          </p>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
          />
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="h-8 w-8 sm:h-9 sm:w-9"
          >
            {isRefreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      <DataFreshnessWarning
        daily={data?.daily}
        dateRange={dateRange}
        loading={loading}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          title="Total Revenue"
          value={formatCurrency(data?.summary?.total_revenue || 0)}
          subtitle={`${formatNumber(data?.revenue?.new_orders || 0)} new + ${formatNumber(data?.revenue?.refill_orders || data?.revenue?.recurring_orders || 0)} refills`}
          icon={DollarSign}
          delta={<DeltaBadge current={data?.summary?.total_revenue} prior={data?.prior_summary?.total_revenue} />}
          loading={loading}
        />
        <SummaryCard
          title="Total Expenses"
          value={formatCurrency(data?.summary?.total_expenses || 0)}
          subtitle={data?.expenses?.ad_spend > 0 ? `COGS + Shipping + Ads` : `COGS + Shipping`}
          icon={Package}
          delta={<DeltaBadge current={data?.summary?.total_expenses} prior={data?.prior_summary?.total_expenses} invert />}
          loading={loading}
        />
        <SummaryCard
          title="Net Revenue"
          value={formatCurrency(data?.summary?.gross_profit || 0)}
          subtitle={`${data?.summary?.margin_percent || 0}% margin`}
          icon={data?.summary?.gross_profit >= 0 ? TrendingUp : TrendingDown}
          delta={<DeltaBadge current={data?.summary?.gross_profit} prior={data?.prior_summary?.gross_profit} />}
          loading={loading}
        />
        <SummaryCard
          title="Total Orders"
          value={formatNumber(data?.summary?.total_orders || 0)}
          subtitle={`${formatNumber(data?.revenue?.new_orders || 0)} new, ${formatNumber(data?.revenue?.recurring_orders || 0)} refills`}
          icon={ShoppingCart}
          delta={<DeltaBadge current={data?.summary?.total_orders} prior={data?.prior_summary?.total_orders} />}
          loading={loading}
        />
      </div>
      
      {/* Revenue Breakdown Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Revenue Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-muted rounded w-full"></div>
                <div className="h-4 bg-muted rounded w-3/4"></div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-muted-foreground shrink-0">New Orders</span>
                  <span className="font-medium text-right">
                    {formatCurrency(data?.revenue?.new_sales || 0)}
                    <span className="text-xs text-muted-foreground ml-1">
                      ({formatNumber(data?.revenue?.new_orders || 0)})
                    </span>
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-muted-foreground shrink-0">Refill Orders</span>
                  <span className="font-medium text-right">
                    {formatCurrency(data?.revenue?.refill_sales || data?.revenue?.recurring_sales || 0)}
                    <span className="text-xs text-muted-foreground ml-1">
                      ({formatNumber(data?.revenue?.refill_orders || data?.revenue?.recurring_orders || 0)})
                    </span>
                  </span>
                </div>
                <div className="border-t pt-2 flex items-center justify-between gap-2">
                  <span className="text-sm font-medium shrink-0">Total</span>
                  <span className="font-bold text-right">{formatCurrency(data?.summary?.total_revenue || 0)}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Expense Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-muted rounded w-full"></div>
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-4 bg-muted rounded w-1/2"></div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-muted-foreground shrink-0">COGS</span>
                  <span className="font-medium text-right">{formatCurrency(data?.expenses?.cogs || 0)}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-muted-foreground shrink-0">Shipping</span>
                  <span className="font-medium text-right">{formatCurrency(data?.expenses?.shipping || 0)}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-muted-foreground shrink-0">Ad Spend</span>
                  <span className={`font-medium text-right ${data?.expenses?.ad_spend ? '' : 'text-muted-foreground italic'}`}>
                    {data?.expenses?.ad_spend ? formatCurrency(data.expenses.ad_spend) : 'N/A'}
                  </span>
                </div>
                <div className="border-t pt-2 flex items-center justify-between gap-2">
                  <span className="text-sm font-medium shrink-0">Total</span>
                  <span className="font-bold text-right">{formatCurrency(data?.summary?.total_expenses || 0)}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      <UnitEconomicsCard
        data={data?.unit_economics}
        prior={data?.prior_unit_economics}
        loading={loading}
      />

      {/* Revenue & Expenses Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">Revenue vs Expenses Trend</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-[300px] flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  interval={Math.floor(chartData.length / 8)}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => formatCurrency(value, true)}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  name="Revenue"
                  fill="hsl(var(--primary) / 0.2)"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="expenses"
                  name="Expenses"
                  stroke="hsl(var(--destructive))"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="profit"
                  name="Net Revenue"
                  stroke="hsl(142 76% 36%)"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="profit_gs_high"
                  name="Net Revenue (GS at Market)"
                  stroke="hsl(45 93% 47%)"
                  strokeWidth={1.5}
                  strokeDasharray="2 4"
                  dot={false}
                  opacity={0.6}
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              No data available for selected period
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Order Count Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">
            <span className="hidden sm:inline">Order Volume (New vs Refill)</span>
            <span className="sm:hidden">Order Volume</span>
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            New = first order, Refill = order_number ≥ 2
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-[250px] flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  interval={Math.floor(chartData.length / 8)}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar 
                  dataKey="newOrders" 
                  name="New Orders" 
                  stackId="orders"
                  fill="hsl(var(--primary))"
                  radius={[0, 0, 0, 0]}
                />
                <Bar 
                  dataKey="refillOrders" 
                  name="Refill Orders" 
                  stackId="orders"
                  fill="hsl(var(--primary) / 0.4)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-muted-foreground">
              No data available for selected period
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Daily Net Revenue Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">Daily Net Revenue</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-[200px] flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  interval={Math.floor(chartData.length / 8)}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => formatCurrency(value, true)}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="profit" 
                  name="Net Revenue"
                  fill="hsl(142 76% 36%)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-muted-foreground">
              No data available for selected period
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Daily Data - Cards on mobile, Table on desktop */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">Daily Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="animate-pulse space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-8 bg-muted rounded"></div>
              ))}
            </div>
          ) : chartData.length > 0 ? (
            <>
              {/* Mobile: Card layout */}
              <div className="sm:hidden space-y-3">
                {chartData.slice().reverse().slice(0, 7).map((day, index) => (
                  <div key={index} className="border rounded-lg p-3 bg-muted/30">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{day.date}</span>
                      <span className="text-xs text-muted-foreground">
                        {day.newOrders} new + {day.refillOrders} refill
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-xs text-muted-foreground">New Sales</div>
                        <div className="font-semibold">{formatCurrency(day.newSales)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Refill Sales</div>
                        <div className="font-semibold">{formatCurrency(day.refillSales)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Net Revenue</div>
                        <div className={`font-semibold ${day.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {formatCurrency(day.profit)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">CAC</div>
                        <div className={`font-semibold ${day.cac > 0 ? (day.cac <= 5000 ? 'text-green-600' : day.cac <= 10000 ? 'text-yellow-600' : 'text-red-600') : 'text-muted-foreground'}`}>
                          {day.cac > 0 ? formatCurrency(day.cac) : '-'}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between mt-2 pt-2 border-t border-muted text-xs text-muted-foreground">
                      <span>COGS {formatCurrency(day.cogs)}</span>
                      <span>Ship {formatCurrency(day.shipping)}</span>
                      <span>Ads {formatCurrency(day.adSpend)}</span>
                    </div>
                  </div>
                ))}
                {chartData.length > 7 && (
                  <p className="text-xs text-muted-foreground text-center">
                    Showing 7 of {chartData.length} days
                  </p>
                )}
              </div>

              {/* Desktop: Table layout */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 font-medium">Date</th>
                      <th className="text-right py-2 px-2 font-medium">New</th>
                      <th className="text-right py-2 px-2 font-medium">Refill</th>
                      <th className="text-right py-2 px-2 font-medium">COGS</th>
                      <th className="text-right py-2 px-2 font-medium">Ship</th>
                      <th className="text-right py-2 px-2 font-medium">Ads</th>
                      <th className="text-right py-2 px-2 font-medium">Net Rev</th>
                      <th className="text-right py-2 px-2 font-medium" title="New + Refill orders">Orders</th>
                      <th className="text-right py-2 px-2 font-medium" title="Customer Acquisition Cost (Ad Spend / New Orders)">CAC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chartData.slice().reverse().slice(0, 10).map((day, index) => (
                      <tr key={index} className="border-b border-muted/50 hover:bg-muted/50">
                        <td className="py-2 px-2">{day.date}</td>
                        <td className="text-right py-2 px-2">
                          <span className="block">{formatCurrency(day.newSales)}</span>
                          <span className="text-xs text-muted-foreground">({day.newOrders})</span>
                        </td>
                        <td className="text-right py-2 px-2">
                          <span className="block">{formatCurrency(day.refillSales)}</span>
                          <span className="text-xs text-muted-foreground">({day.refillOrders})</span>
                        </td>
                        <td className="text-right py-2 px-2 text-muted-foreground">{formatCurrency(day.cogs)}</td>
                        <td className="text-right py-2 px-2 text-muted-foreground">{formatCurrency(day.shipping)}</td>
                        <td className="text-right py-2 px-2 text-muted-foreground">{formatCurrency(day.adSpend)}</td>
                        <td className={`text-right py-2 px-2 font-medium ${day.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {formatCurrency(day.profit)}
                        </td>
                        <td className="text-right py-2 px-2">{day.newOrders + day.refillOrders}</td>
                        <td className={`text-right py-2 px-2 font-medium ${day.cac > 0 ? (day.cac <= 5000 ? 'text-green-600 dark:text-green-400' : day.cac <= 10000 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400') : 'text-muted-foreground'}`}>
                          {day.cac > 0 ? formatCurrency(day.cac) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {chartData.length > 10 && (
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Showing most recent 10 days of {chartData.length} total
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              No data available for selected period
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

